import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { generateQuery } from 'Src/ng2/store/effects/projections/student-groupings';
import { ApiService } from '../../../../shared/services/api-service/api-service';

export interface IFilter {
  filterKey: string;
  label: string;
  filterOptions: Array<{ graphQlKey: string; label: string }>;
}

export interface IGrouping {
  graphQlKey: string;
  label: string;
}

export interface IFocus {
  graphQlKey: string;
  label: string;
  isDefault: boolean;
  category: string;
  categoryOrder: string;
  fociOrder: string;
  filters: IFilter[];
  groupings: IGrouping[];
}

@Injectable()
export class AttendanceDataService {
  private fociCache$: Observable<any> = null;

  constructor(public apiService: ApiService) {}

  getAttendanceFocusData$(schoolId: string, focusKey?: string): Observable<{ data: { AttendanceFocus: IFocus[] } }> {
    const payload = this.getAttendanceFocusQuery(schoolId, focusKey);
    if (!focusKey) {
      if (!this.fociCache$) this.fociCache$ = this.apiService.getStudentsGraphQL(payload).pipe(shareReplay(1));
      return this.fociCache$;
    } else {
      return this.apiService.getStudentsGraphQL(payload);
    }
  }

  getAttendanceGroupingData$(payload: {
    schoolId: string;
    filterKeys: string[];
    groupingKey: string;
    columns: string[];
  }) {
    // only want active students, so we append this filter behind the scenes
    payload.filterKeys.push('ACTIVE_ONLY');

    const query = generateQuery({ payload });
    return this.apiService.getStudentsGraphQL({ query, fetchPolicy: 'no-cache' });
  }

  getAttendanceFocusQuery(schoolId: string, focusKey?: string) {
    const payload = focusKey ? `schoolId:"${schoolId}", focusKey:"${focusKey}"` : `schoolId:"${schoolId}"`;

    const query = `{
      AttendanceFocus(${payload}) {
        graphQlKey
        label
        isDefault
        category
        categoryOrder
        filters {
          label
          filterOptions {
            graphQlKey
            label
            isDefault
          }
        }
        groupings {
          graphQlKey
          label
          requiresWildcard
          groupingSubgroups {
            label
            headerTooltip {
              ...on SimpleTooltip {
                simpleContent
              }
              ...on TableTooltip {
                tableContent {
                  headers
                  rowData
                }
              }
            }
          }
          columns {
            graphQlKey
            label
            requiresWildcard
            columnDataFormat
            cellConfig
            cellTooltip
            headerTooltip {
              ...on SimpleTooltip {
                simpleContent
              }
              ...on TableTooltip {
                tableContent {
                  headers
                  rowData
                }
              }
            }
          }
        }

      }
    }`;
    return { query, fetchPolicy: 'no-cache' };
  }

  clearFociCache(): void {
    this.fociCache$ = null;
  }
}
